<script lang="ts">
  import { viewport } from '../lib/stores/viewport';
  import Icon from './Icon.svelte';
  import { router } from '@inertiajs/svelte';
  import type { Project, ProjectStatus, ProjectsByStatus } from '../lib/types';
  import { categoryColors } from '../lib/utils';
  import {
    isStatusRailOpen,
    statusRailData,
    closeStatusRail,
    draggedProject,
    openProjectRail,
    requestProjectMove,
  } from '../lib/stores/ui';

  interface Props {
    projectsByMonth: Record<string, ProjectsByStatus>;
  }

  let { projectsByMonth }: Props = $props();

  // Derive from store
  let isOpen = $derived($isStatusRailOpen);
  let data = $derived($statusRailData);

  // Derive projects from live props (not stale snapshot)
  let projects = $derived.by(() => {
    if (!data) return [];
    const monthData = projectsByMonth[data.monthKey];
    if (!monthData) return [];
    return monthData[data.status] || [];
  });

  // Drag state for accepting drops
  let isDropTarget = $state(false);

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    if ($draggedProject && data) {
      isDropTarget = true;
    }
  }

  function handleDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      isDropTarget = false;
    }
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDropTarget = false;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId && data) {
      requestProjectMove(projectId, $draggedProject, 'month', {
        monthKey: data.monthKey,
        status: data.status,
      });
    }
  }

  // Dragging projects FROM this rail
  let draggingProjectId = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: Project) {
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }

  // Status icon mapping
  const statusIcons: Record<ProjectStatus, string> = {
    planned: '📋',
    active: '🔄',
    almost: '⏳',
    done: '✅',
  };

  // Status background colors for the header
  const statusHeaderColors: Record<ProjectStatus, string> = {
    planned: 'bg-blue-50 border-blue-200',
    active: 'bg-amber-50 border-amber-200',
    almost: 'bg-orange-50 border-orange-200',
    done: 'bg-green-50 border-green-200',
  };
</script>

{#if isOpen && data}
  <aside class="status-rail" class:in-sheet={$viewport.isMobile} data-sheet-fill>
    <!-- Header -->
    <header class="rail-header {statusHeaderColors[data.status]}" data-sheet-drag-handle>
      <div class="header-title">
        <span class="header-icon">{statusIcons[data.status]}</span>
        <h2 data-sheet-title>{data.statusLabel}</h2>
        <span class="header-count">{projects.length}</span>
      </div>
      <button
        type="button"
        class="close-btn"
        onclick={closeStatusRail}
        title="Close panel"
        data-sheet-close
      >
        {#if $viewport.isMobile}<Icon name="close" />{:else}✕{/if}
      </button>
    </header>

    <!-- Project list (drop zone) -->
    <div
      class="rail-content"
      class:drop-target={isDropTarget}
      ondragover={handleDragOver}
      ondragleave={handleDragLeave}
      ondrop={handleDrop}
      role="list"
      aria-label="{data.statusLabel} projects"
    >
      {#if projects.length === 0}
        <div class="empty-state">
          <span class="empty-icon">📭</span>
          <span class="empty-text">No projects</span>
        </div>
      {:else}
        <div class="project-list">
          {#each projects as project (project.id)}
            <button
              type="button"
              class="project-card {categoryColors[project.category]}"
              class:dragging={draggingProjectId === project.id}
              draggable={!$viewport.isMobile}
              ondragstart={(e) => handleDragStart(e, project)}
              ondragend={handleDragEnd}
              onclick={() => openProjectRail(project.id)}
            >
              <div class="card-content">
                {#if project.isPriority}
                  <span class="star">★</span>
                {/if}
                <span class="card-title">{project.title}</span>
              </div>
            </button>
          {/each}
        </div>
      {/if}
    </div>

    <!-- Footer hint -->
    <footer class="rail-footer">Click to edit • Drag to other columns ←</footer>
  </aside>
{/if}

<style>
  .status-rail {
    width: 12rem;
    background: #f9fafb;
    border-left: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }

  .rail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .header-title {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .header-icon {
    font-size: 0.75rem;
  }

  .header-title h2 {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #374151;
  }

  .header-count {
    font-size: 0.625rem;
    color: #6b7280;
    font-weight: 500;
  }

  .close-btn {
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .close-btn:hover {
    color: #374151;
    background: rgba(0, 0, 0, 0.1);
  }

  .rail-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.375rem;
    transition: background 0.15s;
  }

  .rail-content.drop-target {
    background: #dbeafe;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #9ca3af;
    gap: 0.25rem;
  }

  .empty-icon {
    font-size: 1.25rem;
  }

  .empty-text {
    font-size: 0.5625rem;
  }

  .project-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .project-card {
    display: flex;
    align-items: center;
    padding: 0.25rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    border: 1px solid transparent;
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
  }

  .project-card:hover {
    opacity: 0.85;
  }

  .project-card.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .card-content {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    flex: 1;
  }

  .star {
    color: #facc15;
    font-size: 0.5625rem;
    flex-shrink: 0;
  }

  .card-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2937;
    min-width: 0;
  }

  .rail-footer {
    padding: 0.375rem 0.5rem;
    border-top: 1px solid #e5e7eb;
    background: white;
    font-size: 0.5625rem;
    color: #9ca3af;
    text-align: center;
  }

  /* Mobile: inside the Dashboard's rail bottom sheet. */
  .status-rail.in-sheet {
    flex: 1 1 auto;
    min-height: 0;
    border-left: none;
  }

  .status-rail.in-sheet .rail-content {
    min-height: 0;
    overscroll-behavior: contain;
  }

  .status-rail.in-sheet .header-icon {
    font-size: 1.0625rem;
  }
</style>
